import * as React from "react";
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BiotechIcon from "@mui/icons-material/Biotech";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HPOSelector from "../../projects/components/HPOSelector";

function HPOPrioritization({
  onPrioritize,
  projectId,
  sampleId,
  isAlreadyPrioritized = false,
  isPrioritizing = false,
  selectedHPOTerms = [],
}) {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [internalSelectedTerms, setInternalSelectedTerms] = React.useState([]);
  const [pendingHPOTerms, setPendingHPOTerms] = React.useState([]);

  // Sync internal state with prop changes
  React.useEffect(() => {
    setInternalSelectedTerms(selectedHPOTerms);
  }, [selectedHPOTerms]);

  // Update pending HPO terms when drawer opens
  React.useEffect(() => {
    if (drawerOpen) {
      setPendingHPOTerms(internalSelectedTerms);
    }
  }, [drawerOpen, internalSelectedTerms]);

  const handlePrioritize = () => {
    setInternalSelectedTerms(pendingHPOTerms);
    // Extract only HPO IDs from the terms
    const hpoIds = pendingHPOTerms
      .map((term) =>
        typeof term === "string" ? term : term.id || term.hp || term.code
      )
      .filter(Boolean);
    onPrioritize(hpoIds, sampleId);
    setDrawerOpen(false);
  };

  const handleClear = () => {
    setPendingHPOTerms([]);
  };

  const getButtonState = () => {
    if (isPrioritizing) {
      return {
        icon: <CircularProgress size={20} />,
        text: "Prioritizing...",
        borderColor: "#ff9800",
        color: "#e65100",
        backgroundColor: alpha("#ff9800", 0.08),
        tooltip: "Prioritization is in progress",
      };
    }
    if (isAlreadyPrioritized) {
      return {
        icon: <CheckCircleIcon />,
        text: "Prioritized",
        borderColor: "#4caf50",
        color: "#2e7d32",
        backgroundColor: alpha("#4caf50", 0.08),
        tooltip: "Variants are already prioritized by HPO terms",
      };
    }
    return {
      icon: <BiotechIcon />,
      text: "HPO Prioritization",
      borderColor: "#bdbdbd",
      color: "#333",
      backgroundColor: "transparent",
      tooltip: "",
    };
  };

  const buttonState = getButtonState();

  return (
    <>
      {/* HPO Prioritization Button */}
      <Box
        sx={{
          mb: 1,
          display: "flex",
          gap: 2,
          alignItems: "center",
          position: "relative",
        }}
      >
        <Tooltip title={buttonState.tooltip} arrow>
          <span>
            <Button
              variant="outlined"
              startIcon={buttonState.icon}
              onClick={() => setDrawerOpen(true)}
              disabled={isPrioritizing}
              sx={{
                borderColor: buttonState.borderColor,
                color: buttonState.color,
                fontWeight: 500,
                textTransform: "none",
                background: buttonState.backgroundColor,
                boxShadow: "none",
                "&:hover": {
                  borderColor: isPrioritizing
                    ? "#ff9800"
                    : isAlreadyPrioritized
                    ? "#4caf50"
                    : "#2196f3",
                  backgroundColor: isPrioritizing
                    ? alpha("#ff9800", 0.12)
                    : isAlreadyPrioritized
                    ? alpha("#4caf50", 0.12)
                    : alpha("#2196f3", 0.08),
                  color: isPrioritizing
                    ? "#e65100"
                    : isAlreadyPrioritized
                    ? "#2e7d32"
                    : "#2196f3",
                },
                "&:disabled": {
                  borderColor: buttonState.borderColor,
                  color: buttonState.color,
                  backgroundColor: buttonState.backgroundColor,
                },
              }}
            >
              {buttonState.text}
              {internalSelectedTerms.length > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    backgroundColor: isPrioritizing
                      ? alpha("#ff9800", 0.2)
                      : isAlreadyPrioritized
                      ? alpha("#4caf50", 0.2)
                      : "#e3f2fd",
                    color: isPrioritizing
                      ? "#e65100"
                      : isAlreadyPrioritized
                      ? "#2e7d32"
                      : "#2196f3",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                  }}
                >
                  {internalSelectedTerms.length}
                </Box>
              )}
            </Button>
          </span>
        </Tooltip>
        {/* Processing indicator */}
        {isPrioritizing && (
          <Box
            sx={{
              position: "absolute",
              top: -4,
              right: -4,
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: "#ff9800",
              animation: "pulse 1.5s ease-in-out infinite",
              "@keyframes pulse": {
                "0%, 100%": {
                  opacity: 1,
                },
                "50%": {
                  opacity: 0.5,
                },
              },
            }}
          />
        )}
      </Box>

      {/* HPO Prioritization Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 520 },
            backgroundColor: "#fafbfc",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2.5,
              background: "#f5f5f5",
              color: "#333",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "none",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <BiotechIcon sx={{ fontSize: 26, color: "#2196f3" }} />
              <Typography variant="h6" fontWeight={700}>
                HPO Prioritization
              </Typography>
              {pendingHPOTerms.length > 0 && (
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: "#e3f2fd",
                    color: "#2196f3",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                  }}
                >
                  {pendingHPOTerms.length} selected
                </Box>
              )}
            </Box>
            <IconButton
              onClick={() => setDrawerOpen(false)}
              size="small"
              sx={{
                color: "#333",
                "&:hover": { backgroundColor: alpha("#2196f3", 0.08) },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "white",
                border: "1px solid #bdbdbd",
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select phenotype terms to prioritize variants based on their
                phenotypic associations. This will rank variants by their
                relevance to the selected HPO terms.
              </Typography>

              <HPOSelector
                selectedTerms={pendingHPOTerms}
                onSelectionChange={setPendingHPOTerms}
                maxSelections={10}
              />
            </Paper>
          </Box>

          {/* Footer Actions */}
          <Box
            sx={{
              p: 2,
              backgroundColor: "white",
              borderTop: "2px solid #f0f0f0",
              display: "flex",
              gap: 1.5,
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClear}
              disabled={pendingHPOTerms.length === 0}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderColor: "#bdbdbd",
                color: "#333",
                background: "transparent",
                "&:hover": {
                  borderColor: "#2196f3",
                  color: "#2196f3",
                  backgroundColor: alpha("#2196f3", 0.08),
                },
                "&:disabled": {
                  borderColor: "#e0e0e0",
                  color: "#999",
                },
              }}
            >
              Clear
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={handlePrioritize}
              disabled={pendingHPOTerms.length === 0}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderColor: "#bdbdbd",
                color: "#333",
                background: "transparent",
                "&:hover": {
                  borderColor: "#2196f3",
                  color: "#2196f3",
                  backgroundColor: alpha("#2196f3", 0.08),
                },
                "&:disabled": {
                  borderColor: "#e0e0e0",
                  color: "#999",
                },
              }}
            >
              Prioritize Variants
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}

export default HPOPrioritization;
